import { Controller } from "@hotwired/stimulus"
import { Modal } from "bootstrap"

export default class extends Controller {

    static targets = ["modal", "title", "body"]

    static values = {
        i18nLoading:         { type: String, default: 'Cargando...' },
        i18nError:           { type: String, default: 'Error al cargar los datos.' },
        i18nNoMembers:       { type: String, default: 'No hay miembros.' },
        i18nNoResults:       { type: String, default: 'No hay resultados.' },
        i18nColName:         { type: String, default: 'Nombre' },
        i18nColEmail:        { type: String, default: 'Correo' },
        i18nColPhone:        { type: String, default: 'Teléfono' },
        i18nColStatus:       { type: String, default: 'Estado' },
        i18nActive:          { type: String, default: 'Activo' },
        i18nInactive:        { type: String, default: 'Inactivo' },
        i18nRemoveFromGroup: { type: String, default: 'Quitar del grupo' },
        i18nSearchPlaceholder: { type: String, default: 'Buscar por nombre...' },
        i18nRecords:         { type: String, default: '%count% registros' },
        i18nPaginationPrev:  { type: String, default: '← Anterior' },
        i18nPaginationNext:  { type: String, default: 'Siguiente →' },
        i18nPaginationPage:  { type: String, default: 'Página %page% de %total%' },
    }

    declare readonly modalTarget: HTMLElement
    declare readonly titleTarget: HTMLElement
    declare readonly bodyTarget: HTMLElement
    declare i18nLoadingValue: string
    declare i18nErrorValue: string
    declare i18nNoMembersValue: string
    declare i18nNoResultsValue: string
    declare i18nColNameValue: string
    declare i18nColEmailValue: string
    declare i18nColPhoneValue: string
    declare i18nColStatusValue: string
    declare i18nActiveValue: string
    declare i18nInactiveValue: string
    declare i18nRemoveFromGroupValue: string
    declare i18nSearchPlaceholderValue: string
    declare i18nRecordsValue: string
    declare i18nPaginationPrevValue: string
    declare i18nPaginationNextValue: string
    declare i18nPaginationPageValue: string

    private modalInstance: Modal | null = null
    private boundClick!: (e: Event) => void
    private boundBodyClick!: (e: Event) => void

    // State for paginated children view
    private currentUrl  = ''
    private currentPage = 1
    private currentSearch = ''
    private searchTimer: ReturnType<typeof setTimeout> | null = null

    connect(): void {
        this.boundClick = (e: Event) => {
            const btn = (e.target as Element).closest<HTMLElement>('[data-sede-items-url]')
            if (!btn || !this.element.contains(btn)) return
            const url   = btn.dataset.sedeItemsUrl   ?? ''
            const title = btn.dataset.sedeItemsTitle ?? ''
            this.show({ params: { url, title } })
        }
        // Capture phase: fires before any bubbling handler, regardless of DataTable row insertion
        document.addEventListener('click', this.boundClick, true)

        // Delegated listener on modal body for remove-user and pagination buttons
        this.boundBodyClick = (e: Event) => {
            const target = e.target as Element

            // Remove from group button
            const removeBtn = target.closest<HTMLElement>('.sede-items-remove')
            if (removeBtn && this.bodyTarget.contains(removeBtn)) {
                e.preventDefault()
                void this.removeUser(removeBtn)
                return
            }

            // Pagination buttons
            const pageBtn = target.closest<HTMLElement>('[data-sede-items-page]')
            if (pageBtn && this.bodyTarget.contains(pageBtn)) {
                e.preventDefault()
                const page = parseInt(pageBtn.dataset.sedeItemsPage ?? '1')
                this.currentPage = page
                void this.fetchChildren()
            }
        }
        document.addEventListener('click', this.boundBodyClick, true)
    }

    disconnect(): void {
        document.removeEventListener('click', this.boundClick, true)
        document.removeEventListener('click', this.boundBodyClick, true)
    }

    private getModal(): Modal {
        if (!this.modalInstance) {
            this.modalInstance = new Modal(this.modalTarget)
        }
        return this.modalInstance
    }

    async show({ params }: { params: { url: string; title: string } }): Promise<void> {
        this.currentUrl    = params.url
        this.currentPage   = 1
        this.currentSearch = ''

        this.titleTarget.textContent = params.title
        this.showSpinner()
        this.getModal().show()

        try {
            const res  = await fetch(params.url)
            const data = await res.json()
            this.render(data)
        } catch {
            this.showError()
        }
    }

    private showSpinner(): void {
        this.bodyTarget.innerHTML = `
            <div class="text-center py-4">
                <div class="spinner-border text-primary" role="status">
                    <span class="visually-hidden">${this.i18nLoadingValue}</span>
                </div>
            </div>`
    }

    private showError(): void {
        this.bodyTarget.innerHTML =
            `<p class="text-danger text-center py-3"><i class="bi bi-exclamation-triangle me-1"></i>${this.i18nErrorValue}</p>`
    }

    private async fetchChildren(): Promise<void> {
        const url = new URL(this.currentUrl, window.location.origin)
        url.searchParams.set('page', String(this.currentPage))
        if (this.currentSearch) url.searchParams.set('search', this.currentSearch)

        this.showSpinner()
        try {
            const res  = await fetch(url.toString())
            const data = await res.json()
            this.render(data)
        } catch {
            this.showError()
        }
    }

    private async removeUser(btn: HTMLElement): Promise<void> {
        const removeUrl = btn.dataset.url ?? ''
        if (!removeUrl) return

        const button = btn as HTMLButtonElement
        button.disabled = true
        try {
            const res  = await fetch(removeUrl, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' } })
            const json = await res.json()
            if (json.success) {
                const row = button.closest('tr')
                row?.remove()
                // Re-number rows
                this.bodyTarget.querySelectorAll('tbody tr').forEach((tr, i) => {
                    const first = tr.querySelector('td')
                    if (first) first.textContent = String(i + 1)
                })
            }
        } catch {
            button.disabled = false
        }
    }

    private render(data: any): void {

        if (data.type === 'users') {
            this.renderUsers(data)
        } else {
            this.renderChildren(data)
        }
    }

    private renderUsers(data: { items: any[] }): void {
        if (data.items.length === 0) {
            this.bodyTarget.innerHTML = `<p class="text-muted text-center py-3">${this.i18nNoMembersValue}</p>`
            return
        }

        let html = '<div class="table-responsive"><table class="table table-sm table-striped align-middle mb-0">'
        html += `<thead class="table-light">
                    <tr>
                        <th>#</th>
                        <th>${this.i18nColNameValue}</th>
                        <th>${this.i18nColEmailValue}</th>
                        <th>${this.i18nColPhoneValue}</th>
                        <th>${this.i18nColStatusValue}</th>
                        <th></th>
                    </tr>
                 </thead><tbody>`

        let number = 1
        for (const item of data.items) {
            const badge = item.active
                ? `<span class="badge bg-success">${this.i18nActiveValue}</span>`
                : `<span class="badge bg-secondary">${this.i18nInactiveValue}</span>`
            const editBtn = item.editUrl
                ? `<a href="${item.editUrl}" class="btn btn-warning btn-sm" target="_blank"><i class="bi bi-pencil"></i></a>`
                : ''
            const removeBtn = item.removeUrl
                ? `<button class="btn btn-outline-danger btn-sm ms-1 sede-items-remove" data-url="${item.removeUrl}" title="${this.i18nRemoveFromGroupValue}"><i class="bi bi-x-lg"></i></button>`
                : ''
            const avatar = item.image
                ? `<img src="${item.image}" class="rounded-circle me-2 dt-avatar" style="width:32px;height:32px;object-fit:cover;" alt="">`
                : `<span class="rounded-circle bg-secondary d-inline-flex align-items-center justify-content-center me-2" style="width:32px;height:32px;font-size:13px;color:#fff;">${this.esc(item.name.charAt(0).toUpperCase())}</span>`
            html += `<tr>
                <td>${number++}</td>
                <td><div class="d-flex align-items-center">${avatar}${this.esc(item.name)}</div></td>
                <td><small class="text-muted">${this.esc(item.email)}</small></td>
                <td><small>${this.esc(item.phone ?? '')}</small></td>
                <td>${badge}</td>
                <td class="text-end">${editBtn}${removeBtn}</td>
            </tr>`
        }

        html += '</tbody></table></div>'
        this.bodyTarget.innerHTML = html
    }

    private renderChildren(data: { items: any[]; total?: number; page?: number; totalPages?: number }): void {
        const total      = data.total      ?? data.items.length
        const page       = data.page       ?? 1
        const totalPages = data.totalPages ?? 1

        // Search bar
        const recordsLabel = this.i18nRecordsValue.replace('%count%', total.toLocaleString())
        const searchBar = `
            <div class="px-1 pb-2 d-flex gap-2 align-items-center">
                <input type="search" class="form-control form-control-sm" placeholder="${this.i18nSearchPlaceholderValue}"
                       id="sede-items-search" value="${this.esc(this.currentSearch)}" style="max-width:260px;">
                <span class="text-muted small">${recordsLabel}</span>
            </div>`

        let html = searchBar

        if (data.items.length === 0) {
            html += `<p class="text-muted text-center py-3">${this.i18nNoResultsValue}</p>`
            this.bodyTarget.innerHTML = html
            this.attachSearchListener()
            return
        }

        html += '<div class="table-responsive"><table class="table table-sm table-striped align-middle mb-0">'
        html += `<thead class="table-light">
                    <tr>
                        <th>#</th>
                        <th>${this.i18nColNameValue}</th>
                        <th>${this.i18nColStatusValue}</th>
                        <th></th>
                    </tr>
                 </thead><tbody>`

        const offset = (page - 1) * 50
        let number   = offset + 1
        for (const item of data.items) {
            const badge = item.active
                ? `<span class="badge bg-success">${this.i18nActiveValue}</span>`
                : `<span class="badge bg-secondary">${this.i18nInactiveValue}</span>`
            const editBtn = item.editUrl
                ? `<a href="${item.editUrl}" class="btn btn-warning btn-sm" target="_blank"><i class="bi bi-pencil"></i></a>`
                : ''
            const avatar = item.image
                ? `<img src="${item.image}" class="rounded-circle me-2 dt-avatar" style="width:32px;height:32px;object-fit:cover;" alt="">`
                : `<span class="rounded-circle bg-secondary d-inline-flex align-items-center justify-content-center me-2" style="width:32px;height:32px;font-size:13px;color:#fff;">${this.esc(item.name.charAt(0).toUpperCase())}</span>`
            html += `<tr>
                <td>${number++}</td>
                <td><div class="d-flex align-items-center">${avatar}${this.esc(item.name)}</div></td>
                <td>${badge}</td>
                <td class="text-end">${editBtn}</td>
            </tr>`
        }

        html += '</tbody></table></div>'

        // Pagination controls
        if (totalPages > 1) {
            const prevDisabled = page <= 1 ? 'disabled' : ''
            const nextDisabled = page >= totalPages ? 'disabled' : ''
            const pageLabel = this.i18nPaginationPageValue
                .replace('%page%', String(page))
                .replace('%total%', String(totalPages))
            html += `<div class="d-flex justify-content-between align-items-center mt-2 px-1">
                <button class="btn btn-sm btn-outline-secondary" ${prevDisabled}
                        data-sede-items-page="${page - 1}">${this.i18nPaginationPrevValue}</button>
                <span class="text-muted small">${pageLabel}</span>
                <button class="btn btn-sm btn-outline-secondary" ${nextDisabled}
                        data-sede-items-page="${page + 1}">${this.i18nPaginationNextValue}</button>
            </div>`
        }

        this.bodyTarget.innerHTML = html
        this.attachSearchListener()
    }

    private attachSearchListener(): void {
        const input = this.bodyTarget.querySelector<HTMLInputElement>('#sede-items-search')
        if (!input) return
        input.addEventListener('input', () => {
            if (this.searchTimer) clearTimeout(this.searchTimer)
            this.searchTimer = setTimeout(() => {
                this.currentSearch = input.value.trim()
                this.currentPage   = 1
                void this.fetchChildren()
            }, 300)
        })
        // Focus the input so user can type immediately
        input.focus()
    }

    private esc(str: string): string {
        return str
            .replace(/&/g, '&amp;')
            .replace(/</g, '&lt;')
            .replace(/>/g, '&gt;')
            .replace(/"/g, '&quot;')
    }
}
